export interface Project {
  id: string;
  title: string;
  slug: string;
  client: string;
  clientId?: string;
  year: number;
  coverImage: string;
  disciplines: string[];
  roles: string[];
  tools: string[];
  summary: string;
  colors: string[];
  typography: string[];
  kpis: { label: string; value: string }[];
  press?: { outlet: string; url: string }[];
  gallery: { src: string; alt: string; caption?: string }[];
  beforeAfter?: { before: string; after: string; alt: string };
  featured: boolean;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  quote: string;
  avatar: string;
  projectId?: string;
}

export interface Client {
  id: string;
  name: string;
  logo: string;
  url?: string;
  description?: string;
}

export interface Service {
  id: string;
  name: string;
  summary: string;
  deliverables: string[];
  process: string[];
  startingPrice?: string;
}

// Sample Projects Data
export const projects: Project[] = [
  {
    id: "1",
    title: "Identidade Visual Nova Era",
    slug: "nova-era-branding",
    client: "Nova Era Educação",
    year: 2024,
    coverImage: "/projects/nova-era-cover.jpg",
    disciplines: ["Branding", "Identidade Visual", "Design Gráfico"],
    roles: ["Direção de Arte", "Design de Identidade"],
    tools: ["Adobe Illustrator", "Photoshop", "Figma"],
    summary: "Redesign completo da identidade visual para modernizar a marca e conectar com o público jovem universitário.",
    colors: ["#6366f1", "#8b5cf6", "#06b6d4", "#f59e0b"],
    typography: ["Inter", "Playfair Display"],
    kpis: [
      { label: "Aumento no reconhecimento", value: "+87%" },
      { label: "Engajamento nas redes", value: "+156%" },
      { label: "Conversões no site", value: "+43%" }
    ],
    gallery: [
      { src: "/projects/nova-era-1.jpg", alt: "Logo principal" },
      { src: "/projects/nova-era-2.jpg", alt: "Aplicações da marca" },
      { src: "/projects/nova-era-3.jpg", alt: "Material gráfico" }
    ],
    beforeAfter: {
      before: "/projects/nova-era-before.jpg",
      after: "/projects/nova-era-after.jpg",
      alt: "Comparação logo antigo vs novo"
    },
    featured: true
  },
  {
    id: "2", 
    title: "App EcoRide UI/UX",
    slug: "ecoride-app-design",
    client: "EcoRide Brasil",
    year: 2024,
    coverImage: "/projects/ecoride-cover.jpg",
    disciplines: ["UI/UX Design", "Product Design", "Mobile"],
    roles: ["Lead Designer", "UX Research"],
    tools: ["Figma", "Principle", "Adobe XD"],
    summary: "Design completo do aplicativo de mobilidade sustentável, focando na experiência do usuário e gamificação.",
    colors: ["#22c55e", "#16a34a", "#15803d", "#166534"],
    typography: ["Inter", "SF Pro"],
    kpis: [
      { label: "Downloads no primeiro mês", value: "50k+" },
      { label: "Rating na App Store", value: "4.8/5" },
      { label: "Retenção em 30 dias", value: "68%" }
    ],
    gallery: [
      { src: "/projects/ecoride-1.jpg", alt: "Telas principais do app" },
      { src: "/projects/ecoride-2.jpg", alt: "Fluxo de gamificação" },
      { src: "/projects/ecoride-3.jpg", alt: "Sistema de design" }
    ],
    featured: true
  },
  {
    id: "3",
    title: "Campanha 360° Verão Carioca",
    slug: "campanha-verao-carioca", 
    client: "Rio Turismo",
    year: 2023,
    coverImage: "/projects/verao-cover.jpg",
    disciplines: ["Direção de Arte", "Campanha", "Motion Graphics"],
    roles: ["Diretor de Arte", "Concept Creator"],
    tools: ["Adobe Creative Suite", "Cinema 4D", "After Effects"],
    summary: "Campanha publicitária completa para promover o turismo no Rio durante o verão, com foco na identidade carioca.",
    colors: ["#f97316", "#fb923c", "#fdba74", "#fed7aa"],
    typography: ["Montserrat", "Bebas Neue"],
    kpis: [
      { label: "Alcance da campanha", value: "2.3M" },
      { label: "Aumento no turismo", value: "+28%" },
      { label: "Menções na mídia", value: "47" }
    ],
    gallery: [
      { src: "/projects/verao-1.jpg", alt: "Peças publicitárias" },
      { src: "/projects/verao-2.jpg", alt: "Outdoor e mídia externa" },
      { src: "/projects/verao-3.jpg", alt: "Conteúdo para redes sociais" }
    ],
    featured: true
  },
  {
    id: "4",
    title: "Editorial Revista Arte Hoje",
    slug: "revista-arte-hoje",
    client: "Arte Hoje Magazine",
    year: 2023,
    coverImage: "/projects/editorial-cover.jpg",
    disciplines: ["Design Editorial", "Tipografia", "Layout"],
    roles: ["Designer Editorial", "Direção de Arte"],
    tools: ["Adobe InDesign", "Illustrator", "Photoshop"],
    summary: "Design editorial para revista bimestral de arte contemporânea, criando um sistema flexível e elegante.",
    colors: ["#1f2937", "#374151", "#6b7280", "#d1d5db"],
    typography: ["Minion Pro", "Helvetica Neue"],
    kpis: [
      { label: "Circulação da revista", value: "+45%" },
      { label: "Assinaturas digitais", value: "+89%" },
      { label: "Anos de experiência", value: "16" }
    ],
    gallery: [
      { src: "/projects/editorial-1.jpg", alt: "Capas da revista" },
      { src: "/projects/editorial-2.jpg", alt: "Layouts internos" },
      { src: "/projects/editorial-3.jpg", alt: "Sistema tipográfico" }
    ],
    featured: false
  },
  {
    id: "5",
    title: "Ilustrações Serie Histórias Cariocas",
    slug: "historias-cariocas-ilustracoes",
    client: "Editora Cidade",
    year: 2023,
    coverImage: "/projects/ilustracoes-cover.jpg", 
    disciplines: ["Ilustração", "Arte Digital", "Concept Art"],
    roles: ["Ilustrador", "Concept Artist"],
    tools: ["Procreate", "Adobe Illustrator", "Photoshop"],
    summary: "Série de ilustrações para coleção de livros infantis ambientados no Rio de Janeiro, celebrando a cultura local.",
    colors: ["#3b82f6", "#60a5fa", "#93c5fd", "#dbeafe"],
    typography: ["Comic Neue", "Fredoka One"],
    kpis: [
      { label: "Livros vendidos", value: "25k+" },
      { label: "Escolas parceiras", value: "120" },
      { label: "Exposições", value: "8" }
    ],
    gallery: [
      { src: "/projects/ilustracoes-1.jpg", alt: "Ilustração Cristo Redentor" },
      { src: "/projects/ilustracoes-2.jpg", alt: "Paisagens cariocas" },
      { src: "/projects/ilustracoes-3.jpg", alt: "Personagens da série" }
    ],
    featured: false
  },
  {
    id: "6",
    title: "Motion Graphics Festival de Música",
    slug: "motion-festival-musica",
    client: "Rock in Rio",
    year: 2022,
    coverImage: "/projects/motion-cover.jpg",
    disciplines: ["Motion Graphics", "Direção de Arte", "Vídeo"],
    roles: ["Motion Designer", "Diretor Criativo"],
    tools: ["After Effects", "Cinema 4D", "Premiere Pro"],
    summary: "Criação de motion graphics e identidade visual animada para o maior festival de música da América Latina.",
    colors: ["#dc2626", "#ef4444", "#f87171", "#fca5a5"],
    typography: ["Futura", "Impact"],
    kpis: [
      { label: "Visualizações dos vídeos", value: "15M+" },
      { label: "Alcance global", value: "80 países" },
      { label: "Anos de experiência", value: "16" }
    ],
    gallery: [
      { src: "/projects/motion-1.jpg", alt: "Abertura do festival" },
      { src: "/projects/motion-2.jpg", alt: "Transições entre bandas" },
      { src: "/projects/motion-3.jpg", alt: "Elementos gráficos animados" }
    ],
    featured: true
  }
];

// Sample Testimonials
export const testimonials: Testimonial[] = [
  {
    id: "1",
    name: "Marina Silva",
    role: "Diretora de Marketing",
    company: "Nova Era Educação",
    quote: "O Cássio transformou completamente nossa percepção de marca. O trabalho dele não apenas modernizou nossa identidade, mas criou uma conexão real com nosso público.",
    avatar: "/testimonials/marina.jpg",
    projectId: "1"
  },
  {
    id: "2", 
    name: "Carlos Rodriguez",
    role: "CEO",
    company: "EcoRide Brasil",
    quote: "Raramente vemos um designer que entende tanto de UX quanto de estética. Cássio entregou um app que é bonito e funcional ao mesmo tempo.",
    avatar: "/testimonials/carlos.jpg",
    projectId: "2"
  },
  {
    id: "3",
    name: "Ana Beatriz Costa",
    role: "Coordenadora",
    company: "Rio Turismo",
    quote: "A campanha do Cássio captou a essência do Rio de Janeiro de uma forma única. Os números falam por si só - foi nossa campanha mais bem-sucedida.",
    avatar: "/testimonials/ana.jpg",
    projectId: "3"
  },
  {
    id: "4",
    name: "Roberto Mendes",
    role: "Editor-Chefe",
    company: "Arte Hoje Magazine",
    quote: "Design editorial de altíssimo nível. Cássio conseguiu criar uma identidade visual que respira arte contemporânea.",
    avatar: "/testimonials/roberto.jpg",
    projectId: "4"
  }
];

// Sample Clients
export const clients: Client[] = [
  { id: "1", name: "Nova Era Educação", logo: "/clients/nova-era.png" },
  { id: "2", name: "EcoRide Brasil", logo: "/clients/ecoride.png" },
  { id: "3", name: "Rio Turismo", logo: "/clients/rio-turismo.png" },
  { id: "4", name: "Arte Hoje Magazine", logo: "/clients/arte-hoje.png" },
  { id: "5", name: "Editora Cidade", logo: "/clients/editora-cidade.png" },
  { id: "6", name: "Rock in Rio", logo: "/clients/rock-in-rio.png" },
  { id: "7", name: "Museu de Arte Moderna", logo: "/clients/mam.png" },
  { id: "8", name: "Globo", logo: "/clients/globo.png" },
  { id: "9", name: "Stone", logo: "/clients/stone.png" },
  { id: "10", name: "Magazine Luiza", logo: "/clients/magalu.png" },
  { id: "11", name: "Petrobras", logo: "/clients/petrobras.png" },
  { id: "12", name: "Bradesco", logo: "/clients/bradesco.png" },
  { id: "13", name: "Natura", logo: "/clients/natura.png" },
  { id: "14", name: "Ambev", logo: "/clients/ambev.png" },
  { id: "15", name: "JBS", logo: "/clients/jbs.png" },
  { id: "16", name: "Vale", logo: "/clients/vale.png" }
];

// Sample Services
export const services: Service[] = [
  {
    id: "1",
    name: "Branding & Identidade Visual",
    summary: "Criação e redesign de marcas memoráveis que conectam com o público-alvo",
    deliverables: [
      "Pesquisa e estratégia de marca",
      "Logo e sistema de identidade",
      "Manual de marca",
      "Aplicações práticas"
    ],
    process: [
      "Briefing e pesquisa",
      "Conceituação e estratégia", 
      "Desenvolvimento criativo",
      "Refinamento e entrega"
    ],
    startingPrice: "A partir de R$ 15.000"
  },
  {
    id: "2", 
    name: "UI/UX Design",
    summary: "Design de interfaces digitais intuitivas e experiências memoráveis",
    deliverables: [
      "Pesquisa de usuários",
      "Wireframes e protótipos",
      "Interface design",
      "Sistema de design"
    ],
    process: [
      "Research e análise",
      "Arquitetura da informação",
      "Prototipagem",
      "Testes e refinamento"
    ],
    startingPrice: "A partir de R$ 25.000"
  },
  {
    id: "3",
    name: "Direção de Arte & Campanhas",
    summary: "Conceituação e execução de campanhas publicitárias impactantes",
    deliverables: [
      "Conceito criativo",
      "Direção de arte",
      "Peças publicitárias",
      "Supervisão de produção"
    ],
    process: [
      "Briefing estratégico",
      "Desenvolvimento do conceito",
      "Criação das peças",
      "Produção e finalização"
    ],
    startingPrice: "A partir de R$ 30.000"
  },
  {
    id: "4",
    name: "Design Editorial",
    summary: "Layout e diagramação para publicações impressas e digitais",
    deliverables: [
      "Sistema editorial",
      "Layout das páginas",
      "Tratamento de imagens",
      "Preparação para impressão"
    ],
    process: [
      "Análise do conteúdo",
      "Grid e tipografia",
      "Diagramação",
      "Revisão e fechamento"
    ],
    startingPrice: "A partir de R$ 8.000"
  },
  {
    id: "5",
    name: "Ilustração & Arte Digital",
    summary: "Ilustrações customizadas para diversos tipos de projetos",
    deliverables: [
      "Concept art",
      "Ilustrações finalizadas",
      "Variações e formatos",
      "Licenciamento de uso"
    ],
    process: [
      "Briefing criativo",
      "Sketches e conceitos",
      "Desenvolvimento digital",
      "Refinamento e entrega"
    ],
    startingPrice: "A partir de R$ 3.000"
  },
  {
    id: "6",
    name: "Motion Graphics & Vídeo",
    summary: "Animações e motion design para mídias digitais",
    deliverables: [
      "Storyboard",
      "Animações 2D/3D",
      "Motion graphics",
      "Renderização final"
    ],
    process: [
      "Conceito e storyboard",
      "Animação e motion",
      "Sound design",
      "Renderização e entrega"
    ],
    startingPrice: "A partir de R$ 12.000"
  },
  {
    id: "7",
    name: "IA & Personagens Digitais",
    summary: "Criação e animação de influencers digitais e personagens consistentes com IA",
    deliverables: [
      "Personagens digitais únicos",
      "Sistema de consistência visual",
      "Animações com IA",
      "Treinamento de modelos"
    ],
    process: [
      "Conceituação do personagem",
      "Treinamento de IA",
      "Testes de consistência",
      "Produção automatizada"
    ],
    startingPrice: "A partir de R$ 20.000"
  },
  {
    id: "8",
    name: "Desenvolvimento Fullstack",
    summary: "Sites e aplicações web completas, do design à programação",
    deliverables: [
      "Design UI/UX completo",
      "Frontend responsivo",
      "Backend e APIs",
      "Deploy e manutenção"
    ],
    process: [
      "Arquitetura e design",
      "Desenvolvimento frontend",
      "Backend e integrações",
      "Testes e deploy"
    ],
    startingPrice: "A partir de R$ 35.000"
  },
  {
    id: "9",
    name: "Modelagem 3D Não-Orgânica",
    summary: "Criação de objetos, produtos e ambientes 3D para visualizações",
    deliverables: [
      "Modelagem 3D detalhada",
      "Texturas e materiais",
      "Renderizações realistas",
      "Animações 3D"
    ],
    process: [
      "Conceito e referências",
      "Modelagem base",
      "Materiais e lighting",
      "Render e pós-produção"
    ],
    startingPrice: "A partir de R$ 8.000"
  }
];